import { Icon, Typography } from '@fox/atoms';
import { Flex } from '@fox/layout';
import DOMPurify from 'dompurify';
import * as moment from 'moment';
import { Transition } from 'react-transition-group';
import { TransitionStatus } from 'react-transition-group/Transition';
import styled, { useTheme } from 'styled-components';
import { useComponentStyleContext } from '../component-style-context';
import { NewsListItemModel } from './contracts';
import { transitionStyles } from './shared';
import { StarRating } from './star-rating';

const ExpandedNewsItemsWrapper = styled.div`
	display: grid;
	grid-template-columns: repeat(auto-fit, minmax(500px, max-content));
	padding: 2rem;
	padding-top: 2rem;
	grid-gap: 4rem;
	justify-content: center;
	align-items: flex-start;
`;

const ExpandedNews = styled.div<{ $numberOfReadMoreLines?: number; $showFullReadMore?: boolean }>`
	text-align: left;
	flex-direction: column;
	display: flex;
	padding-bottom: 1rem;
	gap: 0.5rem;
	width: 500px;
	cursor: pointer;
	transition: transform 0.2s ease-in-out;
	.read-more {
		${props =>
			props.$showFullReadMore && !props.$numberOfReadMoreLines ? '' : `-webkit-line-clamp: ${props.$numberOfReadMoreLines ?? 2};`}
		overflow: hidden;
		display: -webkit-box;
		-webkit-box-orient: vertical;
	}
	@media (min-width: 800px) {
		&:hover {
			transform: scale(1.02);
		}
	}
`;

const ResponsiveWrapper = styled.div`
	@media (max-width: 600px) {
		${ExpandedNews} {
			width: 100%;
		}
		${ExpandedNewsItemsWrapper} {
			display: flex;
			width: 100%;
			flex-direction: column;
			padding: 1rem;
			gap: 2rem;
		}
		.text-wrapper,
		.text-flex-wrapper,
		img,
		a {
			width: 100% !important;
		}
		.icon {
			display: none;
		}
	}
`;
function ExpandedNewsItem({
	newsItem,
	openLinkInNewTab,
	hideThumbnail,
	numberOfReadMoreLines,
	showFullReadMore,
	readMoreTextColour
}: {
	newsItem: NewsListItemModel;
	openLinkInNewTab: boolean;
	hideThumbnail?: boolean;
	showFullReadMore?: boolean;
	numberOfReadMoreLines?: number;
	readMoreTextColour?: string;
}) {
	const date = newsItem.publishDateFormatted ?? moment(newsItem.publishDate).format('DD MMM YYYY');
	const theme = useTheme();
	const readMoreHeight = showFullReadMore && numberOfReadMoreLines === null ? 'auto' : `${(numberOfReadMoreLines ?? 2) * 25}px`;
	const { headerFont, headerFontColour, font, fontColour } = useComponentStyleContext();
	return (
		<Transition timeout={200} in={true} appear={true}>
			{(state: TransitionStatus) => {
				return (
					<a
						style={{
							textDecoration: 'none',
							color: 'inherit',
							width: 'max-content',
							display: 'block'
						}}
						href={newsItem.link}
						target={openLinkInNewTab ? '_blank' : '_self'}
					>
						<ExpandedNews
							style={{ ...transitionStyles[state] }}
							$numberOfReadMoreLines={numberOfReadMoreLines}
							$showFullReadMore={showFullReadMore}
						>
							{!hideThumbnail ? (
								<img
									src={newsItem.mobileThumbnailUrl || newsItem.thumbnailUrl}
									style={{
										borderRadius: '10px',
										width: '500px',
										height: '200px',
										objectFit: 'cover'
									}}
								/>
							) : null}
							<Flex
								className="text-flex-wrapper"
								style={{
									justifyContent: 'space-between',
									alignItems: 'center',
									width: '500px'
								}}
							>
								<div className="text-wrapper" style={{ width: '468px' }}>
									<Typography
										variant="h3"
										style={{
											textOverflow: 'ellipsis',
											whiteSpace: 'nowrap',
											overflow: 'hidden',
											fontSize: '1.3rem',
											fontWeight: 'bold',
											...headerFont,
											...headerFontColour
										}}
									>
										{newsItem.truncatedTitle}
									</Typography>
									{newsItem.readMoreSummary?.length || newsItem.summary?.length ? (
										showFullReadMore ? (
											<div
												className="read-more"
												style={{ height: readMoreHeight, marginTop: '0.5rem' }}
												dangerouslySetInnerHTML={{
													__html:
														DOMPurify.sanitize(newsItem.readMoreSummary) || DOMPurify.sanitize(newsItem.summary)
												}}
											/>
										) : (
											<Typography
												className="read-more"
												style={{
													height: readMoreHeight,
													fontSize: '1rem',
													marginTop: '0.5rem',
													...font,
													...fontColour
												}}
											>
												{newsItem.readMoreSummary || newsItem.summary || ''}
											</Typography>
										)
									) : null}
									<Typography
										style={{
											opacity: 0.6,
											textOverflow: 'ellipsis',
											whiteSpace: 'nowrap',
											overflow: 'hidden',
											alignItems: 'center',
											fontSize: '0.8rem',
											display: 'flex',
											justifyContent: 'space-between',
											...font
										}}
									>
										<span>
											{newsItem.tags?.map((tag, i) => {
												return `${tag}${i < newsItem.tags.length - 1 ? ', ' : ''}`;
											})}
											{date && newsItem.publishDateFormatted && newsItem.tags?.length ? <> | </> : null}
											{date && newsItem.publishDateFormatted ? date : null}
										</span>
										{newsItem.averageRating && newsItem.averageRating > 0 ? (
											<span style={{ opacity: 1, flexShrink: 0 }}>
												<StarRating
													rating={newsItem.averageRating}
													customColor={fontColour?.color}
													fontSize="0.8rem"
													fontWeight={600}
												/>
											</span>
										) : null}
									</Typography>
								</div>
								<div className="icon">
									<Icon
										iconName="ArrowForwardRounded"
										customColor={readMoreTextColour || theme.palette.primary.main}
										sx={{ fontSize: '2rem', marginRight: '0.5rem' }}
									/>
								</div>
							</Flex>
						</ExpandedNews>
					</a>
				);
			}}
		</Transition>
	);
}

export function ExpandedView({
	openLinkInNewTab,
	newsItems,
	hideThumbnail,
	numberOfReadMoreLines,
	showFullReadMore,
	readMoreTextColour
}: {
	openLinkInNewTab: boolean;
	newsItems: NewsListItemModel[];
	hideThumbnail?: boolean;
	showFullReadMore?: boolean;
	numberOfReadMoreLines?: number;
	readMoreTextColour?: string;
}) {
	return (
		<ResponsiveWrapper>
			<ExpandedNewsItemsWrapper>
				{newsItems?.map((newsItem, i) => {
					const id = `${i}${newsItem.truncatedTitle}`;
					return (
						<ExpandedNewsItem
							key={id}
							newsItem={newsItem}
							openLinkInNewTab={openLinkInNewTab}
							hideThumbnail={hideThumbnail}
							showFullReadMore={showFullReadMore}
							numberOfReadMoreLines={numberOfReadMoreLines}
							readMoreTextColour={readMoreTextColour}
						/>
					);
				})}
			</ExpandedNewsItemsWrapper>
		</ResponsiveWrapper>
	);
}
